import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RefreshCw, Search, ExternalLink, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { FyreScoreBadge } from '@/components/FyreScoreBadge';
import { DomainHealthDetailsDialog } from '@/components/DomainHealthDetailsDialog';
import { YamlSourceBadge } from '@/components/YamlSourceBadge';

const scoreIssues = (checks) => {
  if (!checks) return [];
  const keys = ['dns_mx', 'ssl_webmail', 'ssl_mail_proxy', 'http_website', 'http_webmail'];
  return keys
    .map((k) => ({ key: k, c: checks[k] }))
    .filter((x) => x.c && x.c.ok === false);
};

const issueLabel = (key) => {
  const map = {
    dns_mx: 'MX',
    ssl_webmail: 'Webmail SSL',
    ssl_mail_proxy: 'Mail-Proxy SSL',
    http_website: 'Website',
    http_webmail: 'Webmail',
  };
  return map[key] || key;
};

export default function CustomersOverviewPage() {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');

  const [customers, setCustomers] = useState([]);
  const [notifByCustomerId, setNotifByCustomerId] = useState({});
  const [notifDialogOpen, setNotifDialogOpen] = useState(false);
  const [notifDialogCustomer, setNotifDialogCustomer] = useState(null);
  const [notifForm, setNotifForm] = useState({ telegram_enabled: false, telegram_chat_id: '' });
  const [notifSaving, setNotifSaving] = useState(false);

  const [domainToCustomerId, setDomainToCustomerId] = useState({});

  const [sortBy, setSortBy] = useState('score');
  const [sortDir, setSortDir] = useState('asc');

  const [selectedDomain, setSelectedDomain] = useState(null);

  const fetchHealth = useCallback(async (force) => {
    try {
      const api = authAxios();
      const qs = force ? '?refresh=true' : '';
      const res = await api.get(`/domains/health${qs}`);
      const list = Array.isArray(res.data?.items) ? res.data.items : [];
      setItems(list);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Domains konnten nicht geladen werden');
      setItems([]);
    }
  }, [authAxios]);

  const fetchCustomersAndSettings = useCallback(async () => {
    try {
      const api = authAxios();
      const [custRes, notifRes] = await Promise.all([
        api.get('/customers'),
        api.get('/customers/notification-settings'),
      ]);
      const custs = Array.isArray(custRes.data) ? custRes.data : [];
      setCustomers(custs);
      const list = Array.isArray(notifRes.data?.items) ? notifRes.data.items : [];
      const map = {};
      for (const it of list) {
        if (!it?.customer_id) continue;
        map[it.customer_id] = {
          telegram_enabled: !!it.telegram_enabled,
          telegram_chat_id: it.telegram_chat_id || '',
          updated_at: it.updated_at || null,
        };
      }
      setNotifByCustomerId(map);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunden/Notifications konnten nicht geladen werden');
      setCustomers([]);
      setNotifByCustomerId({});
    }
  }, [authAxios]);

  const fetchDomainMappings = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get('/domains');
      const list = Array.isArray(res.data) ? res.data : [];
      const map = {};
      for (const it of list) {
        const dom = String(it?.domain || '').trim().toLowerCase();
        const cid = String(it?.customer_id || '').trim();
        if (!dom || !cid) continue;
        map[dom] = cid;
      }
      return map;
    } catch (err) {
      return {};
    }
  }, [authAxios]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchHealth(false),
      fetchCustomersAndSettings(),
      fetchDomainMappings().then((m) => setDomainToCustomerId(m)),
    ]).finally(() => setLoading(false));
  }, [fetchHealth, fetchCustomersAndSettings, fetchDomainMappings]);

  const normalizeLabel = (s) => {
    const base = String(s || '').toLowerCase();
    const noParens = base.replace(/\([^)]*\)/g, ' ');
    return noParens.replace(/\s+/g, ' ').trim();
  };

  const customerIdByName = useMemo(() => {
    const map = {};
    for (const c of customers) {
      const k = normalizeLabel(c?.name);
      if (!k || !c?.id) continue;
      if (!map[k]) map[k] = c.id;
    }
    return map;
  }, [customers]);

  const customerNameById = useMemo(() => {
    const map = {};
    for (const c of customers) {
      if (!c?.id) continue;
      map[c.id] = c?.name || c.id;
    }
    return map;
  }, [customers]);

  const getCustomerIdForItem = (it) => {
    const dom = String(it?.domain || '').trim().toLowerCase();
    if (dom && domainToCustomerId[dom]) return domainToCustomerId[dom];
    const k = normalizeLabel(it?.label);
    return customerIdByName[k] || null;
  };

  const openNotifDialog = (customerId, customerName) => {
    const current = notifByCustomerId[customerId] || { telegram_enabled: false, telegram_chat_id: '' };
    setNotifDialogCustomer({ id: customerId, name: customerName || customerId });
    setNotifForm({ telegram_enabled: !!current.telegram_enabled, telegram_chat_id: current.telegram_chat_id || '' });
    setNotifDialogOpen(true);
  };

  const saveNotif = async () => {
    if (!notifDialogCustomer?.id) return;
    const enabled = !!notifForm.telegram_enabled;
    const chatId = String(notifForm.telegram_chat_id || '').trim();
    if (enabled && !chatId) {
      toast.error('Telegram Chat-ID ist Pflicht, wenn Notifications aktiv sind');
      return;
    }

    setNotifSaving(true);
    try {
      const api = authAxios();
      const payload = {
        telegram_enabled: enabled,
        telegram_chat_id: enabled ? chatId : null,
      };
      const res = await api.put(`/customers/${notifDialogCustomer.id}/notification-settings`, payload);
      const n = res.data || {};
      setNotifByCustomerId((prev) => ({
        ...prev,
        [notifDialogCustomer.id]: {
          telegram_enabled: !!n.telegram_enabled,
          telegram_chat_id: n.telegram_chat_id || '',
          updated_at: n.updated_at || null,
        }
      }));
      toast.success('Telegram-Einstellungen gespeichert');
      setNotifDialogOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setNotifSaving(false);
    }
  };

  const disableNotifQuick = async (customerId) => {
    setNotifSaving(true);
    try {
      const api = authAxios();
      const res = await api.put(`/customers/${customerId}/notification-settings`, {
        telegram_enabled: false,
        telegram_chat_id: null,
      });
      const n = res.data || {};
      setNotifByCustomerId((prev) => ({
        ...prev,
        [customerId]: {
          telegram_enabled: !!n.telegram_enabled,
          telegram_chat_id: n.telegram_chat_id || '',
          updated_at: n.updated_at || null,
        }
      }));
      toast.success('Telegram-Notifications deaktiviert');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setNotifSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = Array.isArray(items) ? items : [];
    const searched = !q
      ? base
      : base.filter((it) => {
          const d = (it.domain || '').toLowerCase();
          const l = (it.label || '').toLowerCase();
          return d.includes(q) || l.includes(q);
        });

    const sorted = [...searched];
    sorted.sort((a, b) => {
      const av = sortBy === 'domain' ? (a.domain || '') : Number(a.score || 0);
      const bv = sortBy === 'domain' ? (b.domain || '') : Number(b.score || 0);
      const dir = sortDir === 'asc' ? 1 : -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return String(a.domain || '').localeCompare(String(b.domain || ''));
    });
    return sorted;
  }, [items, search, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir(key === 'score' ? 'asc' : 'asc');
      return;
    }
    setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
  };

  const handleRefreshAll = async () => {
    setRefreshing(true);
    await fetchHealth(true);
    setRefreshing(false);
    toast.success('Checks aktualisiert');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 text-[#EF694C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-white tracking-tight">
              Kunden-Übersicht
            </h1>
            <YamlSourceBadge className="text-xs" />
          </div>
          <p className="text-sm text-[#a1a1aa] mt-1">
            {items.length} Domains — Fyre-Score (0–100)
          </p>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button className="bg-[#EF694C] hover:bg-[#d65d43] text-white" disabled={refreshing}>
              <RefreshCw className={refreshing ? 'animate-spin' : ''} />
              Alle Domains jetzt prüfen
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#121214] border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Alle Domains prüfen?</AlertDialogTitle>
              <AlertDialogDescription className="text-[#a1a1aa]">
                Das kann je nach Anzahl der Domains ein paar Sekunden dauern. Die Ergebnisse werden für ca. 5 Minuten gecacht.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Abbrechen</AlertDialogCancel>
              <AlertDialogAction className="bg-[#EF694C] hover:bg-[#d65d43]" onClick={handleRefreshAll}>
                Jetzt prüfen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525b]" />
          <Input
            placeholder="Domain oder Kunde suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 pl-10 bg-[#121214] border-white/10 focus:border-[#EF694C] text-white rounded-lg"
          />
        </div>
      </div>

      <Card className="bg-[#121214] border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#a1a1aa] cursor-pointer" onClick={() => toggleSort('domain')}>Domain</TableHead>
                <TableHead className="text-[#a1a1aa]">Kunde</TableHead>
                <TableHead className="text-[#a1a1aa] cursor-pointer" onClick={() => toggleSort('score')}>Fyre-Score</TableHead>
                <TableHead className="text-[#a1a1aa]">SSL Ablauf</TableHead>
                <TableHead className="text-[#a1a1aa]">Status</TableHead>
                <TableHead className="text-[#a1a1aa]">Telegram</TableHead>
                <TableHead className="text-[#a1a1aa] text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((it) => {
                const webmailDays = it?.checks?.ssl_webmail?.days_remaining;
                const issues = scoreIssues(it?.checks);
                const customerId = getCustomerIdForItem(it);
                const customerName = customerId ? (customerNameById[customerId] || customerId) : (it.label || it.domain);
                const notif = customerId ? (notifByCustomerId[customerId] || { telegram_enabled: false, telegram_chat_id: '' }) : null;
                return (
                  <TableRow key={it.domain} className="border-white/5 hover:bg-white/[0.03]">
                    <TableCell className="font-medium text-white">
                      <div className="flex items-center gap-2">
                        <span>{it.domain}</span>
                        <a
                          href={`https://${it.domain}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#a1a1aa] hover:text-white"
                          title="Website öffnen"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#a1a1aa]">
                      {it.label || '-'}
                    </TableCell>
                    <TableCell>
                      <FyreScoreBadge score={it.score} />
                    </TableCell>
                    <TableCell className="text-[#a1a1aa]">
                      {typeof webmailDays === 'number' ? `${webmailDays} Tage` : '-'}
                    </TableCell>
                    <TableCell className="text-[#a1a1aa]">
                      {issues.length === 0 ? (
                        <span className="text-emerald-400">OK</span>
                      ) : (
                        <span className="text-amber-400">
                          {issues.length} Issue{issues.length === 1 ? '' : 's'}: {issues.map((x) => issueLabel(x.key)).join(', ')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-[#a1a1aa]">
                      {customerId ? (
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={!!notif?.telegram_enabled}
                            onCheckedChange={(v) => {
                              if (v) openNotifDialog(customerId, customerName);
                              else disableNotifQuick(customerId);
                            }}
                            disabled={notifSaving}
                          />
                          <button
                            className="text-xs text-[#a1a1aa] hover:text-white"
                            onClick={() => openNotifDialog(customerId, customerName)}
                            disabled={notifSaving}
                          >
                            {notif?.telegram_enabled ? (notif?.telegram_chat_id ? `Chat ${notif.telegram_chat_id}` : 'Chat-ID fehlt') : 'Deaktiviert'}
                          </button>
                        </div>
                      ) : (
                        <span className="text-[#52525b] text-xs">Nicht zugeordnet</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => setSelectedDomain(it.domain)} title="Details anzeigen">
                        <Eye />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={notifDialogOpen} onOpenChange={setNotifDialogOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Telegram Notifications</DialogTitle>
            <DialogDescription className="text-[#a1a1aa]">
              Kunde: {notifDialogCustomer?.name || '-'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={!!notifForm.telegram_enabled}
                onCheckedChange={(v) => setNotifForm((p) => ({ ...p, telegram_enabled: !!v }))}
                disabled={notifSaving}
              />
              <div className="text-sm">Telegram Notifications aktiv</div>
            </div>

            <div>
              <div className="text-xs text-[#a1a1aa] mb-2">Telegram Chat-ID</div>
              <Input
                value={notifForm.telegram_chat_id}
                onChange={(e) => setNotifForm((p) => ({ ...p, telegram_chat_id: e.target.value }))}
                placeholder="z.B. -1001234567890"
                className="h-11 bg-[#0c0c0e] border-white/10 focus:border-[#EF694C] focus:ring-1 focus:ring-[#EF694C] text-white placeholder:text-[#52525b] rounded-lg"
                disabled={notifSaving || !notifForm.telegram_enabled}
              />
              <div className="mt-2 text-xs text-[#a1a1aa]">Pflicht, wenn Notifications aktiv sind.</div>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={saveNotif}
              disabled={notifSaving}
              className="h-11 bg-[#EF694C] hover:bg-[#d65d43] text-white"
            >
              {notifSaving ? 'Speichern…' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DomainHealthDetailsDialog
        open={Boolean(selectedDomain)}
        onOpenChange={(v) => { if (!v) setSelectedDomain(null); }}
        domain={selectedDomain}
      />
    </div>
  );
}
